import type { Container } from 'pixi.js';
import type { GameEventController } from '../types';
import { WinStreakMenuIconView } from './views/WinStreakMenuIconView';
import { startMainMenuWinStreakFlow } from './behaviours/startMainMenuWinStreakFlow';
import type { MetaPopupController } from '../../metaAbstractions/types';
import type { IObservableValue } from '../../mainMenu/types';
import type { WinStreakState } from './WinStreakState';
import { openWinStreakRoadPopup } from './behaviours/openWinStreakRoadPopup';
import type { EventCondition } from '../../app/state/events/types';
import type { ResourceMap } from '../../app/resources/types';
import {
  getCurrentChestProgressByLevel,
  getWinStreakMinLevel,
  winStreakLoseLevel,
  winStreakWinLevel
} from './WinStreakUtils';
import { LoseProgressBaseIconView } from '../../game/views/popups/failFlow/icons/LoseProgressBaseIconView';
import { WinStreakLoseProgressIconView } from '../../game/views/popups/failFlow/icons/WinStreakLoseProgressIconView';

export type WinStreakObservable = IObservableValue<{
  winStreakActiveEvent: WinStreakState | null;
  winStreakEventLastEndTime: number;
  levelIndex: number;
}>;

export interface WinStreakDependencies {
  observable: WinStreakObservable;
  popupsController: MetaPopupController;
  isConditionMet: (condition: EventCondition) => boolean;
  shareImage: ((imageBase64: Promise<string>) => Promise<void>) | null;
  tracking: {
    onEventFinished(eventId: string, progress: number): void;
    onEventStart(eventId: string): void;
  };
  addResources: (resourceMap: ResourceMap, eventId: string) => void;
}

export class WinStreakController implements GameEventController {
  public readonly featureName = 'Win Streak';

  private playLevelAction: (() => Promise<void>) | null = null;

  constructor(private readonly dependencies: WinStreakDependencies) {}

  public async startMainMenuFlow(): Promise<void> {
    await startMainMenuWinStreakFlow({
      dependencies: this.dependencies,
      playLevelAction: this.playLevelAction ?? ((): void => {}),
    });
  }

  public setPlayLevelAction(action: () => Promise<void>): void {
    this.playLevelAction = action;
  }

  public createGameEventIcon(): { icon: WinStreakMenuIconView; unlockLevel: number } {
    const { observable } = this.dependencies;
    const iconView = new WinStreakMenuIconView(observable);

    iconView.onClicked.connect(() => {
      void openWinStreakRoadPopup({
        dependencies: this.dependencies,
        onPlayClicked: async () => {
          await this.playLevelAction?.();
        },
        intro: false,
      });
    });
    return { icon: iconView, unlockLevel: getWinStreakMinLevel() };
  }

  public onLevelWon(): void {
    winStreakWinLevel(this.dependencies.observable);
  }

  public onLevelLost(): void {
    winStreakLoseLevel(this.dependencies.observable);
  }

  public createLostProgressBadge(): Container | null {
    const winStreakState = this.dependencies.observable.getValue().winStreakActiveEvent;

    if (!winStreakState) return null;

    const chestProgress = winStreakState ? getCurrentChestProgressByLevel(winStreakState) : null;

    if (winStreakState && chestProgress && chestProgress.current > 0) {
      return new LoseProgressBaseIconView()
        .injectIcon(
          new WinStreakLoseProgressIconView({
            level: winStreakState.currentProgress,
            currentSteps: chestProgress.current,
            totalSteps: chestProgress.max,
          }),
        )
        .toggleAmountView(false);
    }

    return null;
  }

  public getDebugFunctions(): Record<string, () => void> {
    const { observable } = this.dependencies;

    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      'Increase progress': (): void => {
        winStreakWinLevel(observable);
      },
      'Decrease progress': (): void => {
        winStreakLoseLevel(observable);
      },
      'Unlock next chest': (): void => {
        const state = observable.getValue().winStreakActiveEvent;
        if (!state) return;

        const chest = state.config.chests[state.lastOpenedChestIndex + 1];
        if (!chest) return;

        observable.updateValue({
          winStreakActiveEvent: {
            ...state,
            currentProgress: chest.level,
            lastSeenProgress: chest.level - 1,
          },
        });
      },
      'Set end game': (): void => {
        const state = observable.getValue().winStreakActiveEvent;
        if (!state) return;

        const lastChestIndex = state.config.chests.length - 1;

        observable.updateValue({
          winStreakActiveEvent: {
            ...state,
            finished: false,
            lastOpenedChestIndex: lastChestIndex - 1,
            currentProgress: state.config.chests[lastChestIndex].level - 1,
            lastSeenProgress: state.config.chests[lastChestIndex].level - 2,
          },
        });
      },
      'Set end date to now': (): void => {
        const state = observable.getValue().winStreakActiveEvent;
        if (!state) return;

        observable.updateValue({
          winStreakActiveEvent: {
            ...state,
            eventStartDate: Date.now() - state.config.duration,
          },
        });
      },
      'Clear win streak data': (): void => {
        observable.updateValue({
          winStreakActiveEvent: null,
          winStreakEventLastEndTime: 0,
        });
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
